/**
 * 标准配合数值求解共享最小二乘助手（兼容层）。
 *
 * 文件职责：
 * 1. 纯数值实现已抽取到通用模块 `src/numeric/dampedLeastSquares.ts`
 *    （02 计划 §7.2：装配体与草图共享同一 LM 数值内核，草图不反向依赖装配体）；
 * 2. 本文件保留装配体命名空间的既有函数名，委托通用实现，调用方无需改动。
 */
import {
    calculateMaxAbsoluteResidual,
    calculateResidualSquareCost,
    solveDampedLeastSquaresStep,
    solveLinearSystemWithPartialPivoting,
} from '../../../numeric/dampedLeastSquares';

/**
 * 计算残差平方和成本。
 *
 * @param residuals 残差数组。
 * @returns 成本。
 */
export function calculateStandardMateResidualCost(residuals: readonly number[]): number {
  return calculateResidualSquareCost(residuals);
}

/**
 * 计算最大残差绝对值。
 *
 * @param residuals 残差数组。
 * @returns 最大绝对残差。
 */
export function calculateStandardMateMaxResidual(residuals: readonly number[]): number {
  return calculateMaxAbsoluteResidual(residuals);
}

/**
 * 解小规模线性方程组（列主元高斯消元）。
 *
 * @param matrix 系数矩阵。
 * @param rhs 右侧向量。
 * @param pivotTolerance 主元容差。
 * @returns 解向量；奇异时返回 null。
 */
export function solveStandardMateLinearSystem(
  matrix: readonly (readonly number[])[],
  rhs: readonly number[],
  pivotTolerance: number,
): number[] | null {
  return solveLinearSystemWithPartialPivoting(matrix, rhs, pivotTolerance);
}

/**
 * 计算 Levenberg-Marquardt 阻尼最小二乘参数增量。
 *
 * @param jacobian 雅可比矩阵。
 * @param residuals 残差数组。
 * @param damping 阻尼因子。
 * @returns 参数增量；法方程奇异时返回 null。
 */
export function solveStandardMateDampedLeastSquaresStep(
  jacobian: readonly (readonly number[])[],
  residuals: readonly number[],
  damping: number,
): number[] | null {
  return solveDampedLeastSquaresStep(jacobian, residuals, damping);
}
